/**
 * Platform Admin Sidebar Component
 *
 * Sidebar specifically for platform admin dashboard
 * Uses same pattern as AppSidebar with sidebar-custom class
 */

import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { CBadge, CCloseButton } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilSpeedometer,
  cilBuilding,
  cilPeople,
  cilChartPie,
  cilSettings,
  cilShieldAlt,
  cilClock,
  cilCheckCircle,
  cilXCircle,
  cilSync,
  cilMoney,
  cilFile,
  cilDollar,
  cilCreditCard,
  cilLockLocked,
  cilArrowCircleTop,
  cilEnvelopeClosed,
} from '@coreui/icons'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

import { setSidebarShow } from '../../store/slices/uiSlice'

// Platform admin navigation items
const platformAdminNav = [
  { type: 'title', name: 'Platform Administration' },
  { type: 'item', name: 'Dashboard', to: '/dashboard', icon: cilSpeedometer },
  { type: 'title', name: 'Tenant Management' },
  { type: 'item', name: 'All Tenants', to: '/tenants', icon: cilBuilding },
  { type: 'item', name: 'Pending Approval', to: '/tenants/pending', icon: cilClock, badge: { color: 'warning', text: 'NEW' } },
  { type: 'item', name: 'Verified Tenants', to: '/tenants/verified', icon: cilCheckCircle },
  { type: 'item', name: 'Suspended Tenants', to: '/tenants/suspended', icon: cilXCircle },
  { type: 'title', name: 'Payment Providers' },
  { type: 'item', name: 'Payment Providers', to: '/payment-providers', icon: cilCreditCard },
  { type: 'title', name: 'Billing & Subscriptions' },
  { type: 'item', name: 'Subscription Plans', to: '/subscriptions/plans', icon: cilMoney },
  { type: 'item', name: 'Subscription Requests', to: '/subscriptions/requests', icon: cilArrowCircleTop, badge: { color: 'danger', text: 'NEW' } },
  { type: 'item', name: 'Tenant Billing', to: '/billing', icon: cilFile },
  { type: 'item', name: 'Revenue Reports', to: '/reports/revenue', icon: cilDollar },
  { type: 'title', name: 'System Configuration' },
  { type: 'item', name: 'RBAC Management', to: '/rbac', icon: cilLockLocked },
  { type: 'title', name: 'Analytics & Reports' },
  { type: 'item', name: 'Platform Analytics', to: '/analytics', icon: cilChartPie },
  { type: 'item', name: 'User Statistics', to: '/users/stats', icon: cilPeople },
  { type: 'title', name: 'System' },
  { type: 'item', name: 'Contact Submissions', to: '/contacts', icon: cilEnvelopeClosed },
  { type: 'item', name: 'Job Monitor', to: '/jobs', icon: cilSync },
  { type: 'item', name: 'Platform Settings', to: '/settings', icon: cilSettings },
  { type: 'item', name: 'Security', to: '/security', icon: cilShieldAlt },
]

const PlatformAdminSidebar = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const sidebarShow = useSelector((state) => state.ui?.sidebarShow)

  // Close sidebar on mobile when clicking a nav item
  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      dispatch(setSidebarShow(false))
    }
  }

  // Close sidebar when clicking backdrop
  const handleBackdropClick = () => {
    dispatch(setSidebarShow(false))
  }

  return (
    <>
      {/* Sidebar - using sidebar-custom class like AppSidebar */}
      <div
        className={`sidebar-custom sidebar-dark ${sidebarShow ? 'show' : 'hide'}`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="d-flex align-items-center">
            <CIcon icon={cilShieldAlt} size="xl" className="me-2 text-danger" />
            <span className="fw-bold text-white">EDUNA Admin</span>
          </div>
          <CCloseButton
            className="d-lg-none ms-auto"
            dark
            onClick={() => dispatch(setSidebarShow(false))}
          />
        </div>

        {/* Navigation */}
        <SimpleBar className="sidebar-nav-wrapper">
          <ul style={{ listStyle: 'none', padding: '0.5rem 0', margin: 0 }}>
            {platformAdminNav.map((item, index) => {
              if (item.type === 'title') {
                return (
                  <li key={index} className="nav-title">
                    {item.name}
                  </li>
                )
              }

              const isActive = location.pathname === item.to

              return (
                <li key={index} className={`nav-item-custom ${isActive ? 'active' : ''}`}>
                  <NavLink to={item.to} onClick={handleNavClick}>
                    <CIcon icon={item.icon} className="nav-icon" />
                    <span className="nav-item-text">{item.name}</span>
                    {item.badge && (
                      <CBadge color={item.badge.color} className="ms-auto">
                        {item.badge.text}
                      </CBadge>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </SimpleBar>
      </div>

      {/* Mobile Backdrop */}
      <div
        className={`sidebar-backdrop d-lg-none ${sidebarShow ? 'show' : ''}`}
        onClick={handleBackdropClick}
      />
    </>
  )
}

export default React.memo(PlatformAdminSidebar)
